import { useState, useEffect, useCallback, useRef } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';

const LIST_ENCOUNTERS_QUERY = `
  query ListEncounters(
    $status: String, $search: String, $clinicId: ID, $type: String
    $fromTs: Float, $toTs: Float, $limit: Int, $offset: Int, $onePerPatient: Boolean
  ) {
    listEncounters(
      status: $status, search: $search, clinicId: $clinicId, type: $type
      fromTs: $fromTs, toTs: $toTs, limit: $limit, offset: $offset
      onePerPatient: $onePerPatient
    ) {
      items {
        id
        patientId
        patientName
        clinicId
        type
        status
        scheduledDate
        createdAt
        fileNumber
      }
      total
    }
  }
`;

async function fetchEncounterPage({
  status, search, clinicId, type, fromTs, toTs, limit, offset, onePerPatient,
}) {
  const data = await bffQuery(LIST_ENCOUNTERS_QUERY, {
    status:   status   || null,
    search:   search   || null,
    clinicId: clinicId || null,
    type:     type     || null,
    fromTs:   fromTs   ?? null,
    toTs:     toTs     ?? null,
    limit:    limit    || null,
    offset:   offset   || 0,
    onePerPatient: onePerPatient ? true : null,
  });
  const page = data.listEncounters || { items: [], total: 0 };
  return { items: page.items || [], total: page.total || 0 };
}

// سوابق ویزیت is date-scoped and paged. `fromTs`/`toTs` are epoch-ms bounds (the same
// convention as the accounting report); both null ⇒ today in Tehran. `total` is the
// count across the whole range so the caller can drive page controls.
// `onePerPatient` (PM-0533) makes the rows people rather than appointments: each
// patient once, as their latest visit, and `total` counts patients.
export function useEncounters(
  status = null, search = '', clinicId = null, type = null,
  { fromTs = null, toTs = null, limit = null, offset = 0, onePerPatient = false } = {},
) {
  const [encounters, setEncounters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEncounterPage({ status, search, clinicId, type, fromTs, toTs, limit, offset, onePerPatient })
      .then(page => {
        if (cancelled) return;
        setEncounters(page.items);
        setTotal(page.total);
      })
      .catch(err => { if (!cancelled) { setError(err); setEncounters([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, search, clinicId, type, fromTs, toTs, limit, offset, onePerPatient, nonce]);

  const reload = useCallback(() => setNonce(n => n + 1), []);

  return { encounters, total, loading, error, reload };
}

// The same list as an ACCUMULATING feed, for the desktop's infinite scroll (PM-0533).
//
// Page one replaces the list whenever a filter changes — and until it arrives the
// previous rows stay put (`loading` with rows on screen is the screen's stale-while-
// refetch dimming). `loadMore()` appends the next `pageSize` rows from the offset the
// feed already holds; rows are people, so the offset is a count of people.
//
// Three things keep it from misbehaving while the doctor scrolls and filters:
//   • a generation counter, so an answer that arrives after the filters moved on is
//     dropped instead of being appended to the wrong list;
//   • rows are de-duplicated by id on append, so a visit that landed between two
//     requests cannot show a person twice;
//   • a page that adds nothing marks the feed `exhausted`, so a `total` that drifted
//     above what the server can still serve cannot turn into a request loop.
// A failed `loadMore` keeps every row and surfaces as `moreError`, with `loadMore`
// itself as the retry — only a failed FIRST page is an `error`.
export function useEncounterFeed(
  status = null, search = '', clinicId = null, type = null,
  { fromTs = null, toTs = null, pageSize = 20, onePerPatient = true } = {},
) {
  const [encounters, setEncounters] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [moreError, setMoreError] = useState(null);
  const [exhausted, setExhausted] = useState(false);
  const [nonce, setNonce] = useState(0);

  const generation = useRef(0);
  const known = useRef(new Set()); // ids on screen; its size is the next offset
  const busy = useRef(false);
  const filters = { status, search, clinicId, type, fromTs, toTs, limit: pageSize, onePerPatient };
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const mine = ++generation.current;
    busy.current = false;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setMoreError(null);
    setExhausted(false);
    fetchEncounterPage({ ...filtersRef.current, offset: 0 })
      .then(page => {
        if (mine !== generation.current) return;
        known.current = new Set(page.items.map(e => e.id));
        setEncounters(page.items);
        setTotal(page.total);
      })
      .catch(err => {
        if (mine !== generation.current) return;
        known.current = new Set();
        setError(err); setEncounters([]); setTotal(0);
      })
      .finally(() => { if (mine === generation.current) setLoading(false); });
  }, [status, search, clinicId, type, fromTs, toTs, pageSize, onePerPatient, nonce]);

  const loadMore = useCallback(() => {
    if (busy.current) return;
    const mine = generation.current;
    busy.current = true;
    setLoadingMore(true);
    setMoreError(null);
    fetchEncounterPage({ ...filtersRef.current, offset: known.current.size })
      .then(page => {
        if (mine !== generation.current) return;
        const fresh = page.items.filter(e => !known.current.has(e.id));
        fresh.forEach(e => known.current.add(e.id));
        if (fresh.length) setEncounters(prev => [...prev, ...fresh]); else setExhausted(true);
        setTotal(page.total);
      })
      .catch(err => { if (mine === generation.current) setMoreError(err); })
      .finally(() => {
        if (mine === generation.current) { busy.current = false; setLoadingMore(false); }
      });
  }, []);

  const reload = useCallback(() => setNonce(n => n + 1), []);

  // Scrolling may ask for more only while there is more, nothing is in flight, and
  // the last ask did not fail (a failure waits for the doctor's explicit retry).
  const hasMore = !loading && !loadingMore && !error && !moreError && !exhausted
    && encounters.length < total;

  return { encounters, total, loading, loadingMore, error, moreError, hasMore, loadMore, reload };
}
